import { useCallback, useEffect, useState } from "react";
import { useTenant } from "../../Contexts/TenantContext";
import { productService } from "../../Api/Services/productService";
import { productBrandService } from "../../Api/Services/productBrandService";
import { productCategoryService } from "../../Api/Services/productCategoryService";
import { productModelService } from "../../Api/Services/productModelService";
import { productSpecificationService } from "../../Api/Services/productSpecificationService";
import { productComponentService } from "../../Api/Services/productComponentService";
import { fileUploadService } from "../../Api/Services/fileUploadService";
import { Product, SimplifiedUserProduct } from "../../Models/Product";
import { ProductBrand } from "../../Models/ProductBrand";
import { ProductCategory } from "../../Models/ProductCategory";
import { ProductModel } from "../../Models/ProductModel";
import { ProductSpecification } from "../../Models/ProductSpecification";
import { ProductComponent } from "../../Models/ProductComponent";
import { ProductOrigin } from "../../Enums/ProductOrigin";
import { CommercialUnit } from "../../Enums/CommercialUnit";
import { LengthUnit } from "../../Enums/LengthUnit";
import { WeightUnit } from "../../Enums/WeightUnit";
import { formatPriceBRL } from "../../Utils/DataFormatters";
import { parseCurrencyInput } from "../../Utils/InputParsers";
import { getProductOriginOptions, getProductCommercialUnitLabel } from "../../Utils/Enums";
import { Input } from "../Input/Input";
import { Select } from "../Select/Select";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import SearchBar from "../SearchBar/SearchBar";
import MeasurementInput from "../MeasurementInput/MeasurementInput";
import FileUploader from "../FileUploader/FileUploader";
import Modal from "../common/Modal/Modal";
import Button from "../Button/Button";

type ProductMediaKey =
  | "productImageOne"
  | "productImageTwo"
  | "productImageThree"
  | "productImageFour"
  | "productDocumentOne"
  | "productDocumentTwo";

interface AdminProductDetailsModalProps {
  productId: string | null;
  onClose: () => void;
  onSaved?: () => void;
}

const AdminProductDetailsModal = ({ productId, onClose, onSaved }: AdminProductDetailsModalProps) => {
  const { tenantPreference } = useTenant();

  const isOpen = productId !== null;
  const isNew = productId === "novo";

  const enableProductComponents = tenantPreference?.enableProductComponents ?? true;
  const showFiscalNoteSettings = tenantPreference?.showFiscalNoteSettings;
  const autoGenerateProductCode = tenantPreference?.autoGenerateProductCode ?? false;
  const enableBrandsFeature = tenantPreference?.enableBrandsFeature ?? true;
  const enableCategoriesFeature = tenantPreference?.enableCategoriesFeature ?? true;
  const enableModelsFeature = tenantPreference?.enableModelsFeature ?? true;
  const enableMadeToOrderProducts = tenantPreference?.enableMadeToOrderProducts ?? true;

  const [product, setProduct] = useState<Product>({
    id: "",
    name: "",
    code: "",
    price: 0,
    description: "",
    stockQuantity: 0,
    createdAt: "",
    isActive: true,
    isMadeToOrder: enableMadeToOrderProducts,
    brandId: null,
    categoryId: null,
    modelId: undefined,
    origin: ProductOrigin.National,
    weightValue: null,
    weightUnit: WeightUnit.KG,
    heightValue: null,
    heightUnit: LengthUnit.CM,
    widthValue: null,
    widthUnit: LengthUnit.CM,
    lengthValue: null,
    lengthUnit: LengthUnit.CM,
    unit: CommercialUnit.UN,
    unitsPerBoxText: null,
    ncm: "",
    barcode: null
  });
  const [stockQuantityInput, setStockQuantityInput] = useState<string>("0");
  const [brands, setBrands] = useState<ProductBrand[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [models, setModels] = useState<ProductModel[]>([]);
  const [availableComponents, setAvailableComponents] = useState<SimplifiedUserProduct[]>([]);

  const [productSpecifications, setProductSpecifications] = useState<ProductSpecification[]>([]);
  const [showProductSpecificationForm, setShowProductSpecificationForm] = useState<boolean>(false);
  const [editProductSpecificationId, setEditProductSpecificationId] = useState<string | null>(null);
  const [newProductSpecificationKey, setNewProductSpecificationKey] = useState<string | null>(null);
  const [newProductSpecificationValue, setNewProductSpecificationValue] = useState<string | null>(null);

  const SIZES_STANDARD = ["P", "M", "G", "GG"];
  const SIZES_PLUS = ["G1", "G2", "G3"];
  const ALL_SIZES = [...SIZES_STANDARD, ...SIZES_PLUS];

  const [availableSizes, setAvailableSizes] = useState<string[]>([]);
  const [productComponents, setProductComponents] = useState<ProductComponent[]>([]);
  const [componentSearch, setComponentSearch] = useState<string>("");
  const [selectedComponentIds, setSelectedComponentIds] = useState<string[]>([]);
  const [isAddingComponents, setIsAddingComponents] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [pendingUploads, setPendingUploads] = useState<Record<ProductMediaKey, File | null>>({
    productImageOne: null,
    productImageTwo: null,
    productImageThree: null,
    productImageFour: null,
    productDocumentOne: null,
    productDocumentTwo: null
  });

  const loadBrands = useCallback(async () => {
    if (!enableBrandsFeature) {
      return [];
    }
    const data = await productBrandService.getAll();
    setBrands(data);
    return data;
  }, [enableBrandsFeature]);

  const loadCategories = useCallback(async () => {
    if (!enableCategoriesFeature) {
      return [];
    }
    const data = await productCategoryService.getAll();
    setCategories(data);
    return data;
  }, [enableCategoriesFeature]);

  const loadModels = useCallback(async () => {
    if (!enableModelsFeature) {
      return [];
    }
    const data = await productModelService.getAll();
    setModels(data);
    return data;
  }, [enableModelsFeature]);

  const loadComponents = useCallback(async () => {
    if (!enableProductComponents) {
      setAvailableComponents([]);
      return;
    }
    const data = await productService.getAllActive({ page: 1, pageSize: 100 });
    setAvailableComponents(data.items);
  }, [enableProductComponents]);

  const loadData = useCallback(async () => {
    try {
      if (!isNew && productId) {
        const fetched = await productService.get(productId);
        if (fetched) {
          setProduct({
            ...fetched,
            weightUnit: fetched.weightUnit ?? WeightUnit.KG,
            heightUnit: fetched.heightUnit ?? LengthUnit.CM,
            widthUnit: fetched.widthUnit ?? LengthUnit.CM,
            lengthUnit: fetched.lengthUnit ?? LengthUnit.CM
          });
          setStockQuantityInput(String(fetched.stockQuantity ?? 0));
          setAvailableSizes(fetched.availableSizes ?? []);
          setProductSpecifications(fetched.specifications);
          setProductComponents(enableProductComponents ? fetched.components : []);
          setSelectedComponentIds([]);
        }
      }
      const [brandsData, categoriesData, modelsData] = await Promise.all([
        loadBrands(),
        loadCategories(),
        loadModels()
      ]);
      await loadComponents();
      if (isNew) {
        setProduct(prev => ({
          ...prev,
          brandId: prev.brandId || brandsData[0]?.id || null,
          categoryId: prev.categoryId || categoriesData[0]?.id || null,
          modelId: prev.modelId ?? modelsData[0]?.id ?? null,
          isMadeToOrder: enableMadeToOrderProducts ? prev.isMadeToOrder : false
        }));
      }
    } catch {
      toast.error("Produto não encontrado.");
      onClose();
    }
  }, [enableMadeToOrderProducts, enableProductComponents, isNew, loadBrands, loadCategories, loadComponents, loadModels, onClose, productId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setProduct({
      id: "",
      name: "",
      code: "",
      price: 0,
      description: "",
      stockQuantity: 0,
      createdAt: "",
      isActive: true,
      isMadeToOrder: enableMadeToOrderProducts,
      brandId: null,
      categoryId: null,
      modelId: undefined,
      origin: ProductOrigin.National,
      weightValue: null,
      weightUnit: WeightUnit.KG,
      heightValue: null,
      heightUnit: LengthUnit.CM,
      widthValue: null,
      widthUnit: LengthUnit.CM,
      lengthValue: null,
      lengthUnit: LengthUnit.CM,
      unit: CommercialUnit.UN,
      unitsPerBoxText: null,
      ncm: "",
      barcode: null
    });
    setStockQuantityInput("0");
    setAvailableSizes([]);
    setProductSpecifications([]);
    setProductComponents([]);
    setSelectedComponentIds([]);
    setShowProductSpecificationForm(false);
    setPendingUploads({ productImageOne: null, productImageTwo: null, productImageThree: null, productImageFour: null, productDocumentOne: null, productDocumentTwo: null });
    loadData();
  }, [isOpen, productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPendingUpload = (key: ProductMediaKey, file: File | null) => {
    setPendingUploads(prev => ({ ...prev, [key]: file }));
  };

  const uploadPendingFiles = async (id: string) => {
    const uploadMap: { key: ProductMediaKey; purpose: string; label: string }[] = [
      { key: "productImageOne", purpose: "ProductMainImage", label: "Imagem principal" },
      { key: "productImageTwo", purpose: "ProductImage2", label: "Imagem 2" },
      { key: "productImageThree", purpose: "ProductImage3", label: "Imagem 3" },
      { key: "productImageFour", purpose: "ProductImage4", label: "Imagem 4" },
      { key: "productDocumentOne", purpose: "ProductDocument1", label: "Ficha Técnica" },
      { key: "productDocumentTwo", purpose: "ProductDocument2", label: "Termo de garantia" }
    ];
    for (const { key, purpose, label } of uploadMap) {
      const file = pendingUploads[key];
      if (!file) {
        continue;
      }
      try {
        const uploaded = await fileUploadService.uploadFile({ context: "product", purpose, referenceId: id, file });
        setProduct(prev => ({ ...prev!, [key]: uploaded } as Product));
        setPendingUploads(prev => ({ ...prev, [key]: null }));
      } catch {
        toast.error(`Erro ao enviar ${label.toLowerCase()}.`);
      }
    }
  };

  const handleSaveProduct = async () => {
    if (!product.name?.trim()) {
      toast.error("Informe o nome do produto.");
      return;
    }
    if (!autoGenerateProductCode && !product.code?.trim()) {
      toast.error("Informe o código do produto.");
      return;
    }
    const stockQuantityText = stockQuantityInput.trim();
    if (!stockQuantityText) {
      toast.error("Informe a quantidade em estoque.");
      return;
    }
    const parsedStockQuantity = Number(stockQuantityText);
    if (Number.isNaN(parsedStockQuantity) || parsedStockQuantity < 0) {
      toast.error("Quantidade em estoque inválida.");
      return;
    }
    if (showFiscalNoteSettings !== false) {
      const ncm = product.ncm?.trim() ?? "";
      if (!ncm) {
        toast.error("Informe o NCM do produto.");
        return;
      }
      if (!/^(\d{2}|\d{8})$/.test(ncm)) {
        toast.error("NCM inválido. Use 2 ou 8 dígitos.");
        return;
      }
    }
    const barcode = product.barcode?.trim();
    if (barcode && !/^(\d{8}|\d{12}|\d{13}|\d{14})$/.test(barcode)) {
      toast.error("Código de barras inválido. Use 8, 12, 13 ou 14 dígitos ou deixe em branco para 'SEM GTIN'.");
      return;
    }
    if (product.unit === CommercialUnit.CX && enableProductComponents && !product.unitsPerBoxText?.trim()) {
      toast.error("Informe o texto referente às unidades na caixa.");
      return;
    }
    const normalizedMeasurements = {
      weightValue: product.weightValue ?? null,
      weightUnit: product.weightValue == null ? null : product.weightUnit ?? WeightUnit.KG,
      heightValue: product.heightValue ?? null,
      heightUnit: product.heightValue == null ? null : product.heightUnit ?? LengthUnit.CM,
      widthValue: product.widthValue ?? null,
      widthUnit: product.widthValue == null ? null : product.widthUnit ?? LengthUnit.CM,
      lengthValue: product.lengthValue ?? null,
      lengthUnit: product.lengthValue == null ? null : product.lengthUnit ?? LengthUnit.CM
    };
    const hasPendingUploads = Object.values(pendingUploads).some(Boolean);
    try {
      setIsSaving(true);
      if (product.id) {
        const updated = await productService.update({
          id: product.id,
          name: product.name!,
          code: product.code,
          price: product.price!,
          plusPrice: !enableProductComponents ? (product.plusPrice ?? null) : null,
          availableSizes: !enableProductComponents ? (availableSizes.length > 0 ? availableSizes : null) : null,
          description: product.description!,
          stockQuantity: parsedStockQuantity,
          isActive: product.isActive!,
          isMadeToOrder: enableMadeToOrderProducts ? product.isMadeToOrder! : false,
          brandId: product.brandId || null,
          categoryId: product.categoryId || null,
          modelId: product.modelId || null,
          origin: product.origin,
          ...normalizedMeasurements,
          unit: product.unit,
          unitsPerBoxText: product.unitsPerBoxText,
          ncm: showFiscalNoteSettings !== false ? product.ncm : null,
          barcode: product.barcode
        });
        if (updated) {
          toast.success("Produto atualizado com sucesso!");
          onSaved?.();
          onClose();
        }
      } else {
        const componentIdsForCreate = Array.from(new Set([...productComponents.map(pc => pc.componentId), ...selectedComponentIds]));
        const createPayload = {
          name: product.name!,
          code: autoGenerateProductCode ? undefined : product.code,
          price: product.price!,
          plusPrice: !enableProductComponents ? (product.plusPrice ?? null) : null,
          availableSizes: !enableProductComponents ? (availableSizes.length > 0 ? availableSizes : null) : null,
          description: product.description!,
          stockQuantity: parsedStockQuantity,
          isActive: product.isActive ?? true,
          isMadeToOrder: enableMadeToOrderProducts ? (product.isMadeToOrder ?? true) : false,
          brandId: product.brandId || null,
          categoryId: product.categoryId || null,
          modelId: product.modelId || null,
          origin: product.origin,
          ...normalizedMeasurements,
          unit: product.unit,
          unitsPerBoxText: product.unitsPerBoxText ?? null,
          ncm: showFiscalNoteSettings !== false ? product.ncm! : null,
          barcode: product.barcode ?? null,
          specifications: productSpecifications.map(s => ({ key: s.key, value: s.value })),
          componentIds: componentIdsForCreate
        };
        const created = await productService.create(createPayload);
        if (created) {
          if (hasPendingUploads) {
            await uploadPendingFiles(created);
          }
          toast.success("Produto criado com sucesso!");
          onSaved?.();
          onClose();
        }
      }
    } catch (error: any) {
      const responseData = error?.response?.data;
      const message = responseData?.message
        ?? (responseData?.errors ? Object.values(responseData.errors as Record<string, string[]>).flat().join(", ") : null)
        ?? "Erro ao salvar produto.";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProductSpecification = async () => {
    if (!newProductSpecificationKey || !newProductSpecificationValue) {
      toast.warn("Por favor, preencha todos os campos antes de salvar a especificação.");
      return;
    }
    try {
      if (isNew) {
        if (editProductSpecificationId) {
          setProductSpecifications(prev => prev.map(s => s.id === editProductSpecificationId ? { ...s, key: newProductSpecificationKey, value: newProductSpecificationValue } : s));
        } else {
          setProductSpecifications(prev => [...prev, { id: `temp-${Date.now()}`, key: newProductSpecificationKey, value: newProductSpecificationValue, productId: "" }]);
        }
        toast.success("Especificação adicionada.");
      } else {
        if (editProductSpecificationId) {
          const updated = await productSpecificationService.update({ id: editProductSpecificationId, key: newProductSpecificationKey, value: newProductSpecificationValue, productId: product.id! });
          if (updated) {
            toast.success("Especificação atualizada com sucesso!");
          }
        } else {
          const created = await productSpecificationService.create({ key: newProductSpecificationKey, value: newProductSpecificationValue, productId: product.id! });
          if (created) {
            toast.success("Especificação criada com sucesso!");
          }
        }
        await loadData();
      }
      setNewProductSpecificationKey(null);
      setNewProductSpecificationValue(null);
      setEditProductSpecificationId(null);
      setShowProductSpecificationForm(false);
    } catch {
      toast.error("Erro ao salvar especificação.");
    }
  };

  const handleDeleteProductSpecification = async (specId: string) => {
    try {
      if (isNew) {
        setProductSpecifications(prev => prev.filter(s => s.id !== specId));
        toast.success("Especificação removida.");
      } else {
        const deleted = await productSpecificationService.delete(specId);
        if (deleted) {
          toast.success("Especificação removida com sucesso!");
          loadData();
        }
      }
    } catch {
      toast.error("Erro ao remover especificação.");
    }
  };

  const handleDeleteProductComponent = async (componentId: string, relationId?: string) => {
    try {
      if (isNew) {
        setSelectedComponentIds(prev => prev.filter(cid => cid !== componentId));
        setProductComponents(prev => prev.filter(pc => pc.componentId !== componentId));
        toast.success("Peça removida.");
        return;
      }
      const deleted = await productComponentService.delete(relationId ?? componentId);
      if (deleted) {
        toast.success("Peça removida.");
        await loadData();
      }
    } catch {
      toast.error("Erro ao remover peça.");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, type, value, checked } = target;
    let nextValue: any = value;
    if (name === "ncm") {
      nextValue = value.replace(/\D/g, "").slice(0, 8);
    } else if (name === "barcode") {
      nextValue = value.replace(/\D/g, "").slice(0, 13);
    } else if (name === "origin") {
      nextValue = Number(value);
    } else if (type === "checkbox") {
      nextValue = checked;
    } else if (type === "number") {
      nextValue = Number(value);
    }
    setProduct(prev => ({ ...prev!, [name]: nextValue }));
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(e.target.value) as CommercialUnit;
    setProduct(prev => ({ ...prev!, unit: value, unitsPerBoxText: value === CommercialUnit.CX ? (prev?.unitsPerBoxText ?? null) : null }));
  };

  const handleMeasurementValueChange = (field: "weightValue" | "heightValue" | "widthValue" | "lengthValue", rawValue: string) => {
    const trimmed = rawValue.trim();
    const nextValue = trimmed === "" ? null : Number(trimmed);
    if (trimmed !== "" && Number.isNaN(nextValue)) {
      return;
    }
    setProduct(prev => ({ ...prev!, [field]: nextValue }));
  };

  const toggleWeightUnit = () => {
    setProduct(prev => {
      if (!prev) {
        return prev;
      }
      const currentUnit = prev.weightUnit ?? WeightUnit.KG;
      return { ...prev, weightUnit: currentUnit === WeightUnit.KG ? WeightUnit.G : WeightUnit.KG };
    });
  };

  const toggleLengthUnit = (unitField: "heightUnit" | "widthUnit" | "lengthUnit") => {
    setProduct(prev => {
      if (!prev) {
        return prev;
      }
      const currentUnit = prev[unitField] ?? LengthUnit.CM;
      const nextUnit = currentUnit === LengthUnit.CM ? LengthUnit.M : currentUnit === LengthUnit.M ? LengthUnit.MM : LengthUnit.CM;
      return { ...prev, [unitField]: nextUnit };
    });
  };

  const getLengthUnitLabel = (unit: LengthUnit | null | undefined) => {
    if (unit === LengthUnit.M) {
      return "m";
    }
    if (unit === LengthUnit.MM) {
      return "mm";
    }
    return "cm";
  };

  const usedIds = new Set(productComponents.map(pc => pc.componentId));
  const filteredAvailableComponents = availableComponents.filter(c => {
    if (usedIds.has(c.id)) {
      return false;
    }
    if (product?.id && c.id === product.id) {
      return false;
    }
    const term = componentSearch.trim().toLowerCase();
    if (!term) {
      return true;
    }
    return c.name.toLowerCase().includes(term) || c.code.toLowerCase().includes(term);
  });

  const toggleComponentSelection = (id: string) => {
    setSelectedComponentIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleAddMultipleComponents = async () => {
    if (selectedComponentIds.length === 0) {
      toast.warn("Selecione ao menos uma peça para adicionar.");
      return;
    }
    try {
      if (isNew || !product?.id) {
        const toAdd = selectedComponentIds
          .filter(id => !productComponents.some(pc => pc.componentId === id))
          .map(id => {
            const component = availableComponents.find(c => c.id === id);
            if (!component) {
              return null;
            }
            return { id: `temp-${id}-${Date.now()}`, productId: product.id, componentId: id, component } as unknown as ProductComponent;
          })
          .filter((c): c is ProductComponent => c !== null);
        if (toAdd.length) {
          setProductComponents(prev => [...prev, ...toAdd]);
          toast.success("Peças adicionadas ao rascunho.");
        }
      } else {
        setIsAddingComponents(true);
        const idsToAdd = selectedComponentIds.filter(id => !productComponents.some(pc => pc.componentId === id));
        if (idsToAdd.length === 0) {
          toast.warn("Selecione ao menos uma peça para adicionar.");
          return;
        }
        await productComponentService.createMany({ productId: product.id, componentIds: idsToAdd });
        toast.success("Peças adicionadas com sucesso!");
        await loadData();
      }
      setSelectedComponentIds([]);
      setComponentSearch("");
    } catch {
      toast.error("Erro ao adicionar peças.");
    } finally {
      setIsAddingComponents(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-4xl"
      title={isNew ? "Novo produto" : "Edição de produto"}
    >
      <div className="space-y-4">
        <Input label={"Nome"} type="text" name="name" value={product?.name ?? ""} onChange={handleChange} required />

        {!enableProductComponents && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{"Tamanhos disponíveis"}</label>
            <div className="flex flex-wrap gap-2">
              {ALL_SIZES.map(sz => {
                const selected = availableSizes.includes(sz);
                return (
                  <button
                    key={sz}
                    type="button"
                    onClick={() => setAvailableSizes(prev => prev.includes(sz) ? prev.filter(s => s !== sz) : [...prev, sz])}
                    className={`px-3 py-1 rounded border text-sm font-semibold transition-colors
                      ${selected
                        ? "bg-primary-600 text-white border-primary-600"
                        : "bg-white text-gray-600 border-gray-300 hover:border-primary-500"}`}
                  >
                    {sz}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-gray-400">{"G1, G2, G3 usarão o Preço Plus. Deixe todos desmarcados para não restringir tamanhos."}</p>
          </div>
        )}

        {!autoGenerateProductCode && (
          <Input label={"Código"} type="text" name="code" value={product?.code ?? ""} onChange={handleChange} required />
        )}

        {enableBrandsFeature && (
          <Select label={"Marca"} name="brandId" value={product?.brandId ?? ""} placeholder={"Selecione uma marca"} onChange={handleChange} required options={brands.map(c => ({ value: c.id, label: c.name }))} />
        )}

        <Input label={!enableProductComponents ? "Preço (P, M, G, GG)" : "Preço"} type="text" name="price" value={formatPriceBRL(product?.price ?? 0)} onChange={e => setProduct({ ...product!, price: parseCurrencyInput(e.target.value) })} required />

        {!enableProductComponents && (
          <Input label={"Preço Plus (G1, G2, G3)"} type="text" name="plusPrice" value={formatPriceBRL(product?.plusPrice ?? 0)} onChange={e => setProduct({ ...product!, plusPrice: parseCurrencyInput(e.target.value) || null })} />
        )}

        <Input label={"Descrição"} type="textarea" name="description" value={product?.description ?? ""} rows={5} onChange={handleChange} />

        <Input label={"Quantidade em estoque"} type="number" name="stockQuantity" value={stockQuantityInput} onChange={e => setStockQuantityInput(e.target.value)} required />

        <Input label={"Ativo"} type="checkbox" name="isActive" value={product?.isActive ?? true} onChange={handleChange} />

        {enableMadeToOrderProducts && (
          <Input label={"Disponível sob encomenda"} type="checkbox" name="isMadeToOrder" value={product?.isMadeToOrder ?? true} onChange={handleChange} />
        )}

        {enableCategoriesFeature && (
          <Select label={"Categoria"} name="categoryId" value={product?.categoryId ?? ""} placeholder={"Selecione uma categoria"} onChange={handleChange} required options={categories.map(c => ({ value: c.id, label: c.name }))} />
        )}

        {enableModelsFeature && (
          <Select label={"Modelo"} name="modelId" value={product?.modelId ?? ""} placeholder={"Selecione um modelo"} onChange={handleChange} required options={models.map(c => ({ value: c.id, label: c.name }))} />
        )}

        {showFiscalNoteSettings !== false && (
          <div className="space-y-4">
            <h3 className="text-lg">{"Definições para Nota Fiscal"}</h3>
            <Select label={"Origem do Produto"} name="origin" value={product?.origin ?? ""} onChange={handleChange} required options={getProductOriginOptions()} />
            <Select label={"Unidade Comercial"} name="unit" value={product?.unit ?? ""} onChange={handleUnitChange} required options={[CommercialUnit.UN, CommercialUnit.KG, CommercialUnit.CX, CommercialUnit.MT, CommercialUnit.LT, CommercialUnit.M2, CommercialUnit.M3].map(value => ({ value, label: getProductCommercialUnitLabel(value) }))} />
            {enableProductComponents && product.unit === CommercialUnit.CX && (
              <Input label={"Texto referente às unidades na caixa"} type="text" name="unitsPerBoxText" value={product?.unitsPerBoxText ?? ""} onChange={handleChange} />
            )}
            <Input label="NCM" type="text" name="ncm" value={product?.ncm ?? ""} onChange={handleChange} maxLength={8} required />
            <Input label={"Código de Barras"} type="text" name="barcode" value={product?.barcode ?? ""} onChange={handleChange} maxLength={13} />
          </div>
        )}

        <div>
          <h3 className="text-lg mt-4">{"Dimensões e peso"}</h3>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MeasurementInput label={"Peso"} name="weightValue" value={product?.weightValue ?? ""} unitLabel={product?.weightUnit === WeightUnit.G ? "g" : "kg"} onValueChange={value => handleMeasurementValueChange("weightValue", value)} onUnitToggle={toggleWeightUnit} step={0.01} min={0} />
            <MeasurementInput label={"Altura"} name="heightValue" value={product?.heightValue ?? ""} unitLabel={getLengthUnitLabel(product?.heightUnit)} onValueChange={value => handleMeasurementValueChange("heightValue", value)} onUnitToggle={() => toggleLengthUnit("heightUnit")} step={0.01} min={0} />
            <MeasurementInput label={"Largura"} name="widthValue" value={product?.widthValue ?? ""} unitLabel={getLengthUnitLabel(product?.widthUnit)} onValueChange={value => handleMeasurementValueChange("widthValue", value)} onUnitToggle={() => toggleLengthUnit("widthUnit")} step={0.01} min={0} />
            <MeasurementInput label={"Comprimento"} name="lengthValue" value={product?.lengthValue ?? ""} unitLabel={getLengthUnitLabel(product?.lengthUnit)} onValueChange={value => handleMeasurementValueChange("lengthValue", value)} onUnitToggle={() => toggleLengthUnit("lengthUnit")} step={0.01} min={0} />
          </div>
        </div>

        <div>
          <h3 className="text-lg mt-4">{"Especificações"}</h3>
          {showProductSpecificationForm ? (
            <div className="my-2 flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1 w-full">
                <Input label={"Chave"} type="text" name="key" value={newProductSpecificationKey ?? ""} onChange={e => setNewProductSpecificationKey(e.target.value)} />
              </div>
              <div className="flex-1 w-full">
                <Input label={"Valor"} type="text" name="value" value={newProductSpecificationValue ?? ""} onChange={e => setNewProductSpecificationValue(e.target.value)} />
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => { setShowProductSpecificationForm(false); setEditProductSpecificationId(null); setNewProductSpecificationKey(null); setNewProductSpecificationValue(null); }} className="px-4 py-2">{"CANCELAR"}</Button>
                <Button onClick={handleSaveProductSpecification} className="px-4 py-2">{editProductSpecificationId ? "SALVAR" : "ADICIONAR"}</Button>
              </div>
            </div>
          ) : (
            <button className="flex items-center gap-1 my-2 text-primary-600 hover:text-primary-700" onClick={() => setShowProductSpecificationForm(true)}>
              <FaPlus /> {"Adicionar Especificação"}
            </button>
          )}
          <table className="w-full border-collapse border border-gray-300 text-left mb-4">
            <thead><tr className="bg-primary-120"><th className="border p-3">{"Chave"}</th><th className="border p-3 text-center">{"Valor"}</th><th className="border p-3 text-center">{"Ações"}</th></tr></thead>
            <tbody>
              {productSpecifications.map(d => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="border p-3">{d.key}</td>
                  <td className="border p-3 text-center">{d.value}</td>
                  <td className="border p-3 text-center space-x-2">
                    <button className="text-primary-600 hover:text-primary-700" onClick={() => { setEditProductSpecificationId(d.id); setNewProductSpecificationKey(d.key); setNewProductSpecificationValue(d.value); setShowProductSpecificationForm(true); }}><FaEdit /></button>
                    <button className="text-primary-600 hover:text-primary-700" onClick={() => handleDeleteProductSpecification(d.id)}><FaTrash /></button>
                  </td>
                </tr>
              ))}
              {productSpecifications.length === 0 && <tr><td className="border p-3 text-center text-gray-500" colSpan={3}>{"Nenhuma especificação cadastrada."}</td></tr>}
            </tbody>
          </table>
        </div>

        {enableProductComponents && (
          <div>
            <h3 className="text-lg mt-4">{"Peças"}</h3>
            <div className="my-4 space-y-3">
              <p className="text-sm font-medium text-gray-700">{"Buscar peças (nome ou código)"}</p>
              <SearchBar onSearch={val => setComponentSearch(val)} value={componentSearch} placeholder={"Digite para filtrar"} className="w-full" borderClass="border-gray-800" iconClass="text-gray-800" />
              <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
                {filteredAvailableComponents.length === 0 ? (
                  <p className="text-sm text-gray-600 p-3">{"Nenhuma peça disponível."}</p>
                ) : filteredAvailableComponents.map(c => (
                  <label key={c.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={selectedComponentIds.includes(c.id)} onChange={() => toggleComponentSelection(c.id)} className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{c.name}</p>
                      <p className="text-xs text-gray-600">{"CÓD."} {c.code}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="muted" onClick={() => { setSelectedComponentIds([]); setComponentSearch(""); }} className="px-4 py-2" disabled={isAddingComponents}>{"Limpar seleção"}</Button>
                <Button isLoading={isAddingComponents} onClick={handleAddMultipleComponents} className="px-4 py-2" disabled={isAddingComponents || selectedComponentIds.length === 0}>
                  {`Adicionar ${selectedComponentIds.length || ""} peça(s)`}
                </Button>
              </div>
            </div>
            <table className="w-full border-collapse border border-gray-300 text-left mb-4">
              <thead><tr className="bg-primary-120"><th className="border p-3 text-center">{"Código"}</th><th className="border p-3">{"Nome"}</th><th className="border p-3 text-center">{"Ações"}</th></tr></thead>
              <tbody>
                {productComponents.map(d => (
                  <tr key={d.id} className="hover:bg-gray-50">
                    <td className="border p-3 text-center">{d.component.code}</td>
                    <td className="border p-3">{d.component.name}</td>
                    <td className="border p-3 text-center"><button className="text-primary-600 hover:text-primary-700" onClick={() => handleDeleteProductComponent(d.componentId, d.id)}><FaTrash /></button></td>
                  </tr>
                ))}
                {productComponents.length === 0 && <tr><td className="border p-3 text-center text-gray-500" colSpan={3}>{"Nenhuma peça vinculada."}</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        <div>
          <h3 className="text-lg mt-4">{"Imagens"}</h3>
          <div className="mt-2 flex flex-col sm:flex-row gap-5">
            <FileUploader label={"Imagem principal"} context="product" purpose="ProductMainImage" accept=".jpg,.jpeg,.png" referenceId={product.id} file={product.productImageOne || null} pendingFile={pendingUploads.productImageOne} deferUpload={!product.id} onPendingFileChange={file => setPendingUpload("productImageOne", file)} onUploadComplete={file => setProduct(prev => ({ ...prev!, productImageOne: file }))} onDeleteComplete={() => setProduct(prev => ({ ...prev!, productImageOne: null }))} />
            <FileUploader label={"Imagem 2"} context="product" purpose="ProductImage2" accept=".jpg,.jpeg,.png" referenceId={product.id} file={product.productImageTwo || null} pendingFile={pendingUploads.productImageTwo} deferUpload={!product.id} onPendingFileChange={file => setPendingUpload("productImageTwo", file)} onUploadComplete={file => setProduct(prev => ({ ...prev!, productImageTwo: file }))} onDeleteComplete={() => setProduct(prev => ({ ...prev!, productImageTwo: null }))} />
            <FileUploader label={"Imagem 3"} context="product" purpose="ProductImage3" accept=".jpg,.jpeg,.png" referenceId={product.id} file={product.productImageThree || null} pendingFile={pendingUploads.productImageThree} deferUpload={!product.id} onPendingFileChange={file => setPendingUpload("productImageThree", file)} onUploadComplete={file => setProduct(prev => ({ ...prev!, productImageThree: file }))} onDeleteComplete={() => setProduct(prev => ({ ...prev!, productImageThree: null }))} />
            <FileUploader label={"Imagem 4"} context="product" purpose="ProductImage4" accept=".jpg,.jpeg,.png" referenceId={product.id} file={product.productImageFour || null} pendingFile={pendingUploads.productImageFour} deferUpload={!product.id} onPendingFileChange={file => setPendingUpload("productImageFour", file)} onUploadComplete={file => setProduct(prev => ({ ...prev!, productImageFour: file }))} onDeleteComplete={() => setProduct(prev => ({ ...prev!, productImageFour: null }))} />
          </div>
        </div>

        {enableProductComponents && (
          <div>
            <h3 className="text-lg mt-4">{"Documentos"}</h3>
            <div className="mt-2 flex flex-col sm:flex-row gap-5">
              <FileUploader label={"Ficha Técnica / Desenho Técnico"} context="product" purpose="ProductDocument1" accept=".pdf,.jpg,.jpeg,.png" referenceId={product.id} file={product.productDocumentOne || null} pendingFile={pendingUploads.productDocumentOne} deferUpload={!product.id} onPendingFileChange={file => setPendingUpload("productDocumentOne", file)} onUploadComplete={file => setProduct(prev => ({ ...prev!, productDocumentOne: file }))} onDeleteComplete={() => setProduct(prev => ({ ...prev!, productDocumentOne: null }))} />
              <FileUploader label={"Termo de garantia"} context="product" purpose="ProductDocument2" accept=".pdf,.jpg,.jpeg,.png" referenceId={product.id} file={product.productDocumentTwo || null} pendingFile={pendingUploads.productDocumentTwo} deferUpload={!product.id} onPendingFileChange={file => setPendingUpload("productDocumentTwo", file)} onUploadComplete={file => setProduct(prev => ({ ...prev!, productDocumentTwo: file }))} onDeleteComplete={() => setProduct(prev => ({ ...prev!, productDocumentTwo: null }))} />
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-4">
          <Button variant="secondary" onClick={onClose} className="w-full flex justify-center items-center px-4 py-2">{"CANCELAR"}</Button>
          <Button isLoading={isSaving} onClick={handleSaveProduct} disabled={isSaving} className="w-full flex justify-center items-center px-4 py-2">
            {"SALVAR"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AdminProductDetailsModal;
